import { RowButton } from "@/components/ui/pressable";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

export const CreateFormFieldHint = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <p
    className={cn(
      "flex items-start gap-1.5 text-xs leading-snug text-muted-foreground",
      className,
    )}
  >
    <Info className="mt-0.5 size-3.5 shrink-0 opacity-70" aria-hidden />
    <span>{children}</span>
  </p>
);

export const CreateFormFieldRow = ({
  children,
  columns = 2,
  className,
}: {
  children: ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}) => (
  <div
    className={cn(
      "grid items-start gap-4",
      columns === 1 && "grid-cols-1",
      columns === 2 && "grid-cols-1 sm:grid-cols-2",
      columns === 3 && "grid-cols-1 sm:grid-cols-3",
      className,
    )}
  >
    {children}
  </div>
);

type CreateFormStatusCardProps = {
  active: boolean;
  label: string;
  description: string;
  onClick: () => void;
};

export const CreateFormStatusCard = ({
  active,
  label,
  description,
  onClick,
}: CreateFormStatusCardProps) => (
  <RowButton
    layout="none"
    density="none"
    className={cn(
      "rounded-lg border px-3 py-2.5 text-left transition-colors",
      active
        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
        : "border-border hover:bg-muted/50",
    )}
    onClick={onClick}
  >
    <p className="text-sm font-medium">{label}</p>
    <p className="text-xs text-muted-foreground">{description}</p>
  </RowButton>
);

export const CreateFormSectionLabel = ({
  children,
  required,
}: {
  children: ReactNode;
  required?: boolean;
}) => (
  <p className="text-sm font-medium">
    {children}
    {required ? <span className="text-destructive"> *</span> : null}
  </p>
);

/** Categorized block for create dialogs (Account, Contact, Address, …). */
export const CreateFormSection = ({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section className={cn("space-y-3", className)}>
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0 space-y-0.5">
        <h3 className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        {description ? (
          <p className="text-xs leading-snug text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </header>
    <div className="space-y-4">{children}</div>
  </section>
);

export const CreateFormCollapsible = ({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) => (
  <details
    className="group rounded-md border"
    open={defaultOpen ? true : undefined}
  >
    <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-primary hover:bg-muted/50 [&::-webkit-details-marker]:hidden">
      <span className="group-open:hidden">▸ {label}</span>
      <span className="hidden group-open:inline">▾ {label}</span>
    </summary>
    <div className="space-y-4 border-t px-4 pt-4 pb-4">{children}</div>
  </details>
);

export const CreateFormInfoBanner = ({
  icon: Icon,
  children,
}: {
  icon?: LucideIcon;
  children: ReactNode;
}) => {
  const BannerIcon = Icon ?? Info;
  return (
    <div className="flex gap-2 rounded-md border border-info/40 bg-info/10 px-3 py-2.5 text-sm text-info-text">
      <BannerIcon className="mt-0.5 size-4 shrink-0 opacity-80" aria-hidden />
      <div className="min-w-0 leading-snug">{children}</div>
    </div>
  );
};
